import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from './useAuth'
import { useCurator } from '../contexts/CuratorContext'
import { useAppAccessBlocked, useClientSuspended } from './useAppAccess'
import { useAppForeground } from './useAppForeground'
import { useNoOverlays } from '../lib/overlayGate'
import { shouldTreatForegroundAsStart } from '../lib/startupWindowStart'
import { api, type StartupWindowClaim } from '../services/api'

/**
 * Стартовые окна: один claim на «старт» (холодный маунт после всех гейтов и
 * после закрытия сторис; возврат из фона после долгой паузы). Сервер сам
 * выбирает окно и фиксирует показ — здесь только решение «когда спросить».
 */
export function useStartupWindow() {
  const { user, isAuthenticated, needsOnboarding, isCurator } = useAuth()
  const { viewingClientId, isAdminView } = useCurator()
  const accessBlocked = useAppAccessBlocked()
  const suspended = useClientSuspended()
  const noOverlays = useNoOverlays()

  const [pending, setPending] = useState<StartupWindowClaim | null>(null)
  const inFlight = useRef(false)
  const coldDone = useRef(false)
  const lastSeenAt = useRef<number | null>(null)

  const u = user as Record<string, unknown> | undefined
  const gates = isAuthenticated && !!u && !isCurator && !viewingClientId && !isAdminView
    && !needsOnboarding && !suspended && !accessBlocked && !!u.onboardingCompleted
    && (!u.type || u.type === 'client')

  const claim = useCallback((trigger: 'cold' | 'foreground') => {
    if (inFlight.current || pending) return
    inFlight.current = true
    api.claimStartupWindow(trigger)
      .then((res) => { if (res?.window) setPending(res.window) })
      .catch(() => { /* окно — не критичный путь; молча пропускаем старт */ })
      .finally(() => { inFlight.current = false })
  }, [pending])

  // Холодный старт: когда гейты пройдены и оверлеев нет. Settle-таймер —
  // сторис монтируются в том же коммите, их эффект регистрации должен успеть
  // отработать до решения хоста.
  useEffect(() => {
    if (coldDone.current || !gates || !noOverlays) return
    const t = setTimeout(() => {
      if (coldDone.current) return
      coldDone.current = true
      lastSeenAt.current = Date.now()
      claim('cold')
    }, 300)
    return () => clearTimeout(t)
  }, [gates, noOverlays, claim])

  // Пока документ виден — обновляем метку «последний раз видели» раз в минуту.
  useEffect(() => {
    if (!gates) return
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') lastSeenAt.current = Date.now()
    }, 60_000)
    return () => clearInterval(id)
  }, [gates])

  useAppForeground(() => {
    const now = Date.now()
    const isStart = shouldTreatForegroundAsStart(lastSeenAt.current, now)
    lastSeenAt.current = now
    if (isStart && gates && noOverlays && coldDone.current) claim('foreground')
  }, gates)

  const clear = useCallback(() => setPending(null), [])
  return { pending, clear }
}
